import { Alert, Accordion, Box, Group, Stack, Text } from '@mantine/core';
import { IconCode, IconInfoCircle } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useRetrieveOutputContracts from '@/hooks/api/steps/useRetrieveOutputContracts.ts';
import {
  buildOutputContractByOptionValue,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/dataInputSchemaContractUtils.ts';
import {
  getDataInputOptionValue,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/hooks/useDataInputOptions.ts';
import useDataInputOptions from '@/pages/Editor/components/SharedFields/DataInputSelector/hooks/useDataInputOptions.ts';
import {
  getCodeInputBindingError,
  getDuplicateBindingIndexes,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/codeInputBindings.ts';
import { DataInput, OutputAvailability } from '@/types/automationFields.ts';

type Props = {
  dataInputs: DataInput[];
  stepId: string;
  updateDataInputs: (dataInputs: DataInput[]) => void;
};

const EDIT_KEYS_ACCORDION_VALUE = 'edit-code-input-keys';

const getCodeInputKeyError = (bindingName: string): string | undefined =>
  getCodeInputBindingError(bindingName)?.replaceAll('Code variable name', 'Code input key') ?? undefined;

/**
 * Displays the persisted dictionary keys used by supplied Transform code.
 * The shared input selector remains responsible for assigning missing keys;
 * this component makes that runtime contract understandable where code is edited.
 */
const TransformCodeInputKeys = ({ dataInputs, stepId, updateDataInputs }: Props) => {
  const { options } = useDataInputOptions(stepId);
  const { data: outputContracts } = useRetrieveOutputContracts();
  const [accordionValue, setAccordionValue] = useState<string | null>(null);

  const optionByValue = useMemo(
    () => new Map(options.flatMap((group) => group.items).map((option) => [option.value, option])),
    [options],
  );
  const outputContractByValue = useMemo(() => {
    const contracts = outputContracts?.upstream_output_contracts?.[stepId] ?? [];
    return buildOutputContractByOptionValue(contracts);
  }, [outputContracts?.upstream_output_contracts, stepId]);
  const duplicateIndexes = useMemo(() => getDuplicateBindingIndexes(dataInputs), [dataInputs]);

  const rows = useMemo(
    () =>
      dataInputs.map((dataInput, index) => {
        const optionValue = getDataInputOptionValue(dataInput.config);
        const option = optionByValue.get(optionValue);
        const bindingName = dataInput.binding_name ?? '';
        const error =
          getCodeInputKeyError(bindingName) ||
          (duplicateIndexes.has(index) ? 'Code input key must be unique.' : undefined);
        return {
          bindingName,
          displayName:
            dataInput.name || option?.label || dataInput.last_known_display_name || `Input ${index + 1}`,
          error,
          index,
          isOptional:
            outputContractByValue.get(optionValue)?.contract.availability ===
            OutputAvailability.RuntimeOptional,
        };
      }),
    [dataInputs, duplicateIndexes, optionByValue, outputContractByValue],
  );

  const hasErrors = rows.some((row) => row.error);
  const optionalRows = rows.filter((row) => row.isOptional && row.bindingName);

  const onBindingNameChange = useCallback(
    (bindingName: string, index: number) => {
      const nextInputs = [...dataInputs];
      nextInputs[index] = { ...nextInputs[index], binding_name: bindingName.trim() || null };
      updateDataInputs(nextInputs);
    },
    [dataInputs, updateDataInputs],
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <Stack gap="xs">
      {optionalRows.length > 0 && (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />} title="Optional code inputs">
          {optionalRows.length === 1 ? 'This input may not be produced on every run.' : 'These inputs may not be produced on every run.'}{' '}
          Check {optionalRows.map((row) => `inputs["${row.bindingName}"].availability`).join(' and ')} before reading <code>.data</code>.
        </Alert>
      )}

      <Box>
        <Group gap="xs" mb={4}>
          <IconCode size={16} />
          <Text fw={600} size="sm">
            Code input keys
          </Text>
        </Group>
        <Text c="dimmed" size="xs" mb="xs">
          Use these stable keys to access the selected inputs in your code.
        </Text>
        <Stack gap={4}>
          {rows.map((row) => (
            <Group key={`${row.index}-${row.bindingName}`} justify="space-between" wrap="nowrap">
              <Text size="sm" truncate="end">
                {row.displayName}
              </Text>
              <Text component="code" ff="monospace" size="xs" style={{ whiteSpace: 'nowrap' }}>
                {`inputs["${row.bindingName || 'missing'}"]`}
              </Text>
            </Group>
          ))}
        </Stack>
      </Box>

      <Accordion value={hasErrors ? EDIT_KEYS_ACCORDION_VALUE : accordionValue} onChange={setAccordionValue} variant="contained">
        <Accordion.Item value={EDIT_KEYS_ACCORDION_VALUE}>
          <Accordion.Control>{hasErrors ? 'Fix code input keys' : 'Edit code input keys'}</Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
                Changing a key does not update your code.
              </Alert>
              {rows.map((row) => (
                <StyledTextInput
                  key={row.index}
                  label={row.displayName}
                  value={row.bindingName}
                  placeholder="input_name"
                  onChange={(event) => onBindingNameChange(event.currentTarget.value, row.index)}
                  error={row.error}
                  required
                />
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
};

export default TransformCodeInputKeys;
